import { Link } from "react-router-dom";
import { ArrowRight, Layout, Grid3x3, ScrollText, PanelLeft, Sparkles } from "lucide-react";

// Hub — landing page listing the 4 dropship redesign options.
// Reached at /preview/dropship-options.

const OPTIONS = [
  {
    n: 1,
    id: "marketplace",
    icon: Grid3x3,
    title: "Marketplace",
    subtitle: "Shopify Collective / Spocket style",
    tagline: "Left filters · center product grid · right rail with pinned actions",
    strengths: ["Discovery-first — best for buyers browsing", "Visual product cards", "Filters always visible"],
    weakness:  "Denser · more visual noise for daily operators",
    path: "/preview/dropship-options/1",
    accent: "from-blue-500/10 to-blue-500/0",
  },
  {
    n: 2,
    id: "bento",
    icon: Layout,
    title: "Bento dashboard",
    subtitle: "Faire supplier / Notion home style",
    tagline: "No tabs · modular cards of varying sizes on one scrollable page",
    strengths: ["At-a-glance operating view", "Feels custom and premium", "Easy to extend with more widgets"],
    weakness:  "Less structured — power users may need to hunt for specifics",
    path: "/preview/dropship-options/2",
    accent: "from-purple-500/10 to-purple-500/0",
  },
  {
    n: 3,
    id: "single-page",
    icon: ScrollText,
    title: "Single-page flow",
    subtitle: "Stripe Dashboard / Linear / Vercel style",
    tagline: "One long scrollable page · sticky right-side anchor nav",
    strengths: ["Cleanest, most 'professional SaaS' feel", "Zero tabs — everything is one page", "Great for cross-linking (deep-link to any section)"],
    weakness:  "Long page — needs the anchor nav to feel fast",
    path: "/preview/dropship-options/3",
    accent: "from-emerald-500/10 to-emerald-500/0",
  },
  {
    n: 4,
    id: "workspace",
    icon: PanelLeft,
    title: "Workspace split",
    subtitle: "Linear / Front / Intercom style",
    tagline: "Left partner list · right relationship detail with inner tabs",
    strengths: ["Perfectly mirrors your 'shared object' data model", "Deep-focus per relationship", "Best for daily ops (one partner = one workspace)"],
    weakness:  "Needs to select a partner to see anything — no cross-partner overview by default",
    path: "/preview/dropship-options/4",
    accent: "from-amber-500/10 to-amber-500/0",
  },
];

export default function DropshipOptionsHub() {
  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-amber-500" /> Dropship redesign · choose a direction
          </div>
          <h1 className="text-4xl font-semibold tracking-tight">4 layout directions</h1>
          <p className="text-sm text-zinc-600 mt-3 max-w-2xl leading-relaxed">
            Each option is a genuinely different layout paradigm — not just a tab reshuffle. Open any one and toggle
            <span className="mx-1 inline-block bg-zinc-900 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">Seller ⇄ Buyer</span>
            at the top to see both portals. All options preserve the same rules: verqo is the source of truth,
            agreement must be signed before catalog access, buyer channels sync via connectors, orders flow back
            to the seller pipeline.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {OPTIONS.map(o => {
            const Icon = o.icon;
            return (
              <Link
                key={o.id}
                to={o.path}
                data-testid={`option-${o.n}`}
                className="rounded-2xl bg-white border border-zinc-200 hover:border-zinc-900 hover:shadow-xl transition-all overflow-hidden group"
              >
                <div className={`h-32 bg-gradient-to-br ${o.accent} border-b border-zinc-100 flex items-center justify-center relative overflow-hidden`}>
                  <Icon className="w-14 h-14 text-zinc-800/70 group-hover:scale-110 transition-transform" />
                  <div className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Option {o.n}</div>
                </div>
                <div className="p-6">
                  <h2 className="text-xl font-semibold tracking-tight">{o.title}</h2>
                  <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">{o.subtitle}</div>
                  <p className="text-sm text-zinc-700 mt-3">{o.tagline}</p>

                  <div className="mt-4 space-y-1.5">
                    {o.strengths.map((s, i) => (
                      <div key={i} className="text-xs text-zinc-600 flex items-start gap-2">
                        <span className="text-emerald-600 mt-0.5">+</span> {s}
                      </div>
                    ))}
                    <div className="text-xs text-zinc-500 flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5">−</span> {o.weakness}
                    </div>
                  </div>

                  <div className="mt-5 pt-5 border-t border-zinc-100 flex items-center justify-between">
                    <span className="text-[11px] font-mono text-zinc-400">{o.path}</span>
                    <span className="text-xs font-semibold text-zinc-900 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      Open preview <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 rounded-2xl border border-dashed border-zinc-300 p-5 bg-white/50">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Design rules — same across all 4 options</div>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-zinc-700">
            <div>· verqo is the source of truth for MSRP · MAP · Wholesale · Handling</div>
            <div>· Buyer signs an agreement <em>before</em> catalog access is granted</div>
            <div>· Buyer connects Shopify / Woo / Amazon / Walmart / CSV / Webhook</div>
            <div>· Orders push back to the seller&apos;s order pipeline automatically</div>
            <div>· Invoice + auto-debit on delivery per agreement terms</div>
            <div>· Claims open automatically on MAP violation / stalled tracking</div>
          </div>
        </div>
      </div>
    </div>
  );
}
